/**
 * 画像アップロードユーティリティ
 *
 * upl_img_for_utage_web API へ raw binary body で画像をアップロードする。
 * 既存 apiClient は JSON body 専用のため、このモジュールで独立実装する。
 * ブラウザから STF サーバーへ直接リクエストする（CORS は STF 側で許可済み）。
 */
import { useAuthStore } from "~/stores/useAuthStore";
import { API_ERROR_CODES, ApiError, type ApiResponse } from "~/types/api";

const STF_URL = import.meta.env.DEV ? "/stf" : import.meta.env.VITE_STF_URL;

/** アバター画像 img_cat 定数 */
const IMG_CAT_AVATAR = "3";

export type UploadAvatarResponse = {
  image_id: string;
};

/**
 * アバター画像をアップロードし、image_id を返す。
 *
 * @param blob - アップロードする JPEG Blob
 * @returns image_id 文字列
 * @throws ApiError - userId が取得できない、または API エラー時に throw。
 */
export const uploadAvatar = async (blob: Blob): Promise<string> => {
  const userId = useAuthStore.getState().userId;

  if (!userId) {
    useAuthStore.getState().logout();
    throw new ApiError(API_ERROR_CODES.INVALID_TOKEN, "No userId");
  }

  const url = `${STF_URL}/api=upl_img_for_utage_web&img_cat=${IMG_CAT_AVATAR}&user_id=${encodeURIComponent(userId)}`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded; charset=utf-8",
      },
      body: blob,
    });
  } catch {
    throw new ApiError(API_ERROR_CODES.UNKNOWN, "Network error");
  }

  if (!response.ok) {
    throw new ApiError(response.status, `HTTP error: ${response.status}`);
  }

  let json: ApiResponse<UploadAvatarResponse>;
  try {
    json = (await response.json()) as ApiResponse<UploadAvatarResponse>;
  } catch {
    throw new ApiError(API_ERROR_CODES.UNKNOWN, "Failed to parse response");
  }

  if (json.code === API_ERROR_CODES.SUCCESS) {
    return json.data.image_id;
  }

  if (json.code === API_ERROR_CODES.INVALID_TOKEN) {
    useAuthStore.getState().logout();
    throw new ApiError(json.code, "Invalid token");
  }

  throw new ApiError(json.code, `API error: ${json.code}`);
};
